import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import Input from '../ui/Input';
import Button from '../ui/Button';
import AuthLayout from './AuthLayout';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const { FiMail, FiLock, FiUser, FiBriefcase } = FiIcons;

const SignUp = () => {
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        tenantName: '',
        tenantSlug: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
    });

    const { registerTenant } = useAuth();
    const navigate = useNavigate();

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')   // remove special chars
            .replace(/\s+/g, '-')           // spaces → hyphen
            .replace(/-+/g, '-');           // remove duplicate hyphens
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'tenantName') {
            setFormData({
                ...formData,
                tenantName: value,
                tenantSlug: generateSlug(value),
            });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const getPasswordStrength = (pass) => {
        if (!pass) return 0;
        let strength = 0;
        if (pass.length >= 8) strength += 25;
        if (/[A-Z]/.test(pass)) strength += 25;
        if (/[0-9]/.test(pass)) strength += 25;
        if (/[^A-Za-z0-9]/.test(pass)) strength += 25;
        return strength;
    };

    const strength = getPasswordStrength(formData.password);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (strength < 75) {
            toast.error('Please use a stronger password');
            return;
        }

        setLoading(true);

        try {
            console.log('Registering tenant with data:', formData);
            let result;

            result = await registerTenant(
                formData.tenantName,
                formData.tenantSlug,
                formData.email,
                formData.password,
                formData.firstName,
                formData.lastName
            );

            if (result.success) {
                toast.success('Tenant registered successfully!');
                navigate('/');
            } else {
                toast.error(result.error || 'Failed to sign up');
            }
        } catch (error) {
            toast.error('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title={"Create account"}
            subtitle={"Start your 14-day free trial today."}
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                    label="Company/Tenant Name"
                    placeholder="Acme Corp"
                    name="tenantName"
                    value={formData.tenantName}
                    onChange={handleChange}
                    icon={FiBriefcase}
                    required
                />

                <Input
                    label="Tenant Slug"
                    name="tenantSlug"
                    value={formData.tenantSlug}
                    readOnly
                />

                <div className="grid grid-cols-2 gap-4">
                    <Input
                        label="First Name"
                        placeholder="John"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        icon={FiUser}
                        required
                    />
                    <Input
                        label="Last Name"
                        placeholder="Doe"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                    />
                </div>

                <Input
                    label="Email Address"
                    placeholder="name@company.com"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    icon={FiMail}
                    required
                />

                <div className="space-y-2">
                    <Input
                        label="Password"
                        placeholder="••••••••"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        icon={FiLock}
                        required
                    />
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500 dark:text-gray-400">Password strength</span>
                            <span className={strength >= 75 ? "text-green-500 font-medium" : strength >= 50 ? "text-yellow-500 font-medium" : "text-red-500 font-medium"}>
                                {strength >= 75 ? 'Strong' : strength >= 50 ? 'Fair' : 'Weak'}
                            </span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${strength >= 75 ? 'bg-green-500' : strength >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${strength}%` }}
                            />
                        </div>
                    </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                    By signing up, you agree to our{' '}
                    <Link to="/terms" className="text-indigo-600 hover:underline dark:text-indigo-400">Terms of Service</Link> and{' '}
                    <Link to="/privacy" className="text-indigo-600 hover:underline dark:text-indigo-400">Privacy Policy</Link>.
                </p>

                <Button type="submit" className="w-full" isLoading={loading}>
                    {'Create account'}
                </Button>

                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                        Sign in
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
};

export default SignUp;